import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@material-ui/core/styles';
import CssBaseline from '@material-ui/core/CssBaseline';

// Layouts
import AdminLayout from './layouts/AdminLayout';
import AuthLayout from './layouts/AuthLayout';

// Auth Pages
import LoginPage from './pages/auth/LoginPage';
import ForgotPasswordPage from './pages/auth/ForgotPasswordPage';
import TwoFactorAuthPage from './pages/auth/TwoFactorAuthPage';

// Dashboard Pages
import DashboardPage from './pages/dashboard/DashboardPage';
import MainDashboard from './pages/dashboard/MainDashboard';
import BookingsDashboard from './pages/dashboard/BookingsDashboard';
import FinancialDashboard from './pages/dashboard/FinancialDashboard';
import TripsDashboard from './pages/dashboard/TripsDashboard';
import VehiclesDashboard from './pages/dashboard/VehiclesDashboard';

// Users Pages
import UsersListPage from './pages/users/UsersListPage';
import UserCreatePage from './pages/users/UserCreatePage';
import UserDetailPage from './pages/users/UserDetailPage';
import UserEditPage from './pages/users/UserEditPage';
import UserDeactivatePage from './pages/users/UserDeactivatePage';

// Companies Pages
import CompaniesListPage from './pages/companies/CompaniesListPage';
import CompanyCreatePage from './pages/companies/CompanyCreatePage';
import CompanyDetailPage from './pages/companies/CompanyDetailPage';
import CompanyEditPage from './pages/companies/CompanyEditPage';
import CompanyDeactivatePage from './pages/companies/CompanyDeactivatePage';

// Vehicles Pages
import VehiclesListPage from './pages/vehicles/VehiclesListPage';
import VehicleCreatePage from './pages/vehicles/VehicleCreatePage';
import VehicleDetailPage from './pages/vehicles/VehicleDetailPage';
import VehicleEditPage from './pages/vehicles/VehicleEditPage';
import VehicleDeactivatePage from './pages/vehicles/VehicleDeactivatePage';

// Routes Pages
import RoutesListPage from './pages/routes/RoutesListPage';
import RouteCreatePage from './pages/routes/RouteCreatePage';
import RouteDetailPage from './pages/routes/RouteDetailPage';
import RouteEditPage from './pages/routes/RouteEditPage';
import RouteDeactivatePage from './pages/routes/RouteDeactivatePage';

// Trips Pages
import TripsListPage from './pages/trips/TripsListPage';
import TripCreatePage from './pages/trips/TripCreatePage';
import TripDetailPage from './pages/trips/TripDetailPage';
import TripEditPage from './pages/trips/TripEditPage';
import TripCancelPage from './pages/trips/TripCancelPage';

// Bookings Pages
import BookingsListPage from './pages/bookings/BookingsListPage';
import BookingCreatePage from './pages/bookings/BookingCreatePage';
import BookingDetailPage from './pages/bookings/BookingDetailPage';
import BookingEditPage from './pages/bookings/BookingEditPage';
import BookingCancelPage from './pages/bookings/BookingCancelPage';

// Coupons Pages
import CouponsListPage from './pages/coupons/CouponsListPage';
import CouponCreatePage from './pages/coupons/CouponCreatePage';
import CouponDetailPage from './pages/coupons/CouponDetailPage';
import CouponEditPage from './pages/coupons/CouponEditPage';

// Commissions Pages
import CommissionsListPage from './pages/commissions/CommissionsListPage';
import CommissionCreatePage from './pages/commissions/CommissionCreatePage';
import CommissionDetailPage from './pages/commissions/CommissionDetailPage';
import CommissionEditPage from './pages/commissions/CommissionEditPage';

// Transactions Pages
import TransactionsListPage from './pages/transactions/TransactionsListPage';
import TransactionCreatePage from './pages/transactions/TransactionCreatePage';
import TransactionDetailPage from './pages/transactions/TransactionDetailPage';
import TransactionEditPage from './pages/transactions/TransactionEditPage';

// Settings Pages
import SettingsPage from './pages/settings/SettingsPage';

// Identity Verification
import IdentityVerificationPage from './pages/identity_verification/IdentityVerificationPage';
import SubmitVerificationPage from './pages/identity_verification/SubmitVerificationPage';
import VerificationRequired from './pages/identity_verification/VerificationRequired';

// Context
import { AuthProvider } from './contexts/AuthContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { SettingsProvider } from './contexts/SettingsContext';
import { BookingProvider } from './contexts/BookingContext';
import { CompanyProvider } from './contexts/CompanyContext';
import { CouponProvider } from './contexts/CouponContext';
import { TripProvider } from './contexts/TripContext';
import { VehicleProvider } from './contexts/VehicleContext';

// Guards
import PrivateRoute from './components/guards/PrivateRoute';
import AdminRoute from './components/guards/AdminRoute';

const App = () => {
  const [darkMode, setDarkMode] = useState(false);
  
  // Modern theme — refined palette, typography, shape, shadows
  const theme = createTheme({
    palette: {
      type: darkMode ? 'dark' : 'light',
      primary: {
        light: '#6366f1',
        main: '#4f46e5',
        dark: '#3730a3',
        contrastText: '#ffffff',
      },
      secondary: {
        light: '#f472b6',
        main: '#ec4899',
        dark: '#be185d',
        contrastText: '#ffffff',
      },
      success: { main: '#10b981', light: '#34d399', dark: '#047857' },
      warning: { main: '#f59e0b', light: '#fbbf24', dark: '#b45309' },
      error: { main: '#ef4444', light: '#f87171', dark: '#b91c1c' },
      info: { main: '#0ea5e9', light: '#38bdf8', dark: '#0369a1' },
      background: {
        default: darkMode ? '#0b1020' : '#f5f7fb',
        paper: darkMode ? '#111733' : '#ffffff',
      },
      text: {
        primary: darkMode ? '#e6e8ef' : '#111827',
        secondary: darkMode ? '#9aa3b2' : '#4b5563',
      },
      divider: darkMode ? 'rgba(255,255,255,0.08)' : 'rgba(17,24,39,0.08)',
    },
    typography: {
      fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
      h1: { fontWeight: 700, letterSpacing: '-0.02em' },
      h2: { fontWeight: 700, letterSpacing: '-0.02em' },
      h3: { fontWeight: 700, letterSpacing: '-0.01em' },
      h4: { fontWeight: 700, letterSpacing: '-0.01em' },
      h5: { fontWeight: 600 },
      h6: { fontWeight: 600 },
      button: { textTransform: 'none', fontWeight: 600 },
    },
    shape: { borderRadius: 12 },
    overrides: {
      MuiCssBaseline: {
        '@global': {
          body: {
            scrollbarColor: darkMode ? '#374151 #111733' : '#cbd5e1 #f5f7fb',
          },
        },
      },
      MuiPaper: {
        rounded: { borderRadius: 16 },
        elevation1: {
          boxShadow: darkMode
            ? '0 1px 2px rgba(0,0,0,0.4)'
            : '0 1px 2px rgba(17,24,39,0.04), 0 1px 3px rgba(17,24,39,0.06)',
        },
        elevation2: {
          boxShadow: darkMode
            ? '0 4px 12px rgba(0,0,0,0.4)'
            : '0 4px 12px rgba(17,24,39,0.06), 0 2px 4px rgba(17,24,39,0.04)',
        },
        elevation3: {
          boxShadow: darkMode
            ? '0 10px 30px rgba(0,0,0,0.5)'
            : '0 10px 30px rgba(17,24,39,0.08), 0 4px 12px rgba(17,24,39,0.06)',
        },
      },
      MuiButton: {
        root: {
          borderRadius: 10,
          paddingTop: 8,
          paddingBottom: 8,
          paddingLeft: 18,
          paddingRight: 18,
        },
        contained: {
          boxShadow: 'none',
          '&:hover': { boxShadow: '0 6px 18px rgba(79,70,229,0.25)' },
        },
        containedPrimary: {
          background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
        },
      },
      MuiAppBar: {
        colorPrimary: {
          background: darkMode
            ? 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)'
            : 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
          color: '#ffffff',
        },
      },
      MuiCard: {
        root: {
          borderRadius: 16,
          border: darkMode
            ? '1px solid rgba(255,255,255,0.06)'
            : '1px solid rgba(17,24,39,0.06)',
        },
      },
      MuiTableHead: {
        root: {
          backgroundColor: darkMode ? 'rgba(255,255,255,0.03)' : '#f8fafc',
        },
      },
      MuiTableCell: {
        head: { fontWeight: 700, color: darkMode ? '#cbd5e1' : '#334155' },
      },
      MuiChip: {
        root: { fontWeight: 600 },
      },
      MuiOutlinedInput: {
        root: { borderRadius: 10 },
      },
      MuiListItem: {
        root: {
          borderRadius: 10,
          margin: '2px 8px',
          width: 'auto',
          '&$selected': {
            backgroundColor: darkMode
              ? 'rgba(99,102,241,0.18)'
              : 'rgba(79,70,229,0.10)',
            color: darkMode ? '#c7d2fe' : '#4f46e5',
            '& .MuiListItemIcon-root': { color: darkMode ? '#c7d2fe' : '#4f46e5' },
          },
          '&:hover': {
            backgroundColor: darkMode
              ? 'rgba(255,255,255,0.04)'
              : 'rgba(79,70,229,0.06)',
          },
        },
      },
      MuiTableContainer: {
        root: {
          borderRadius: 16,
          border: darkMode
            ? '1px solid rgba(255,255,255,0.06)'
            : '1px solid rgba(17,24,39,0.06)',
        },
      },
      MuiTableRow: {
        root: {
          transition: 'background-color 150ms ease',
          '&:hover': {
            backgroundColor: darkMode
              ? 'rgba(255,255,255,0.03)'
              : 'rgba(79,70,229,0.04)',
          },
          '&$selected, &$selected:hover': {
            backgroundColor: darkMode
              ? 'rgba(99,102,241,0.12)'
              : 'rgba(79,70,229,0.08)',
          },
        },
      },
      MuiTableCell: {
        root: {
          borderBottom: darkMode
            ? '1px solid rgba(255,255,255,0.06)'
            : '1px solid rgba(17,24,39,0.06)',
          fontSize: '0.9rem',
          padding: '14px 16px',
        },
        head: {
          fontWeight: 700,
          fontSize: '0.78rem',
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          color: darkMode ? '#cbd5e1' : '#475569',
          padding: '14px 16px',
        },
      },
      MuiDialog: {
        paper: {
          borderRadius: 18,
          padding: 4,
        },
      },
      MuiDialogTitle: {
        root: { fontWeight: 700, fontSize: '1.15rem' },
      },
      MuiSnackbarContent: {
        root: { borderRadius: 12, fontWeight: 500 },
      },
      MuiTab: {
        root: { textTransform: 'none', fontWeight: 600, fontSize: '0.92rem' },
      },
      MuiTabs: {
        indicator: { height: 3, borderRadius: 3 },
      },
      MuiTextField: {
        root: { marginTop: 4, marginBottom: 4 },
      },
      MuiAvatar: {
        root: { fontWeight: 700 },
      },
      MuiIconButton: {
        root: {
          borderRadius: 10,
          '&:hover': {
            backgroundColor: darkMode
              ? 'rgba(255,255,255,0.06)'
              : 'rgba(79,70,229,0.08)',
          },
        },
      },
      MuiTooltip: {
        tooltip: {
          backgroundColor: darkMode ? '#1e293b' : '#0f172a',
          fontSize: '0.78rem',
          padding: '6px 10px',
          borderRadius: 8,
        },
      },
      MuiAlert: {
        root: { borderRadius: 12, fontWeight: 500 },
      },
      MuiLinearProgress: {
        root: { borderRadius: 999, height: 6 },
        bar: { borderRadius: 999 },
      },
      MuiSwitch: {
        root: { padding: 7 },
      },
    },
  });

  // Toggle dark mode
  const toggleDarkMode = () => {
    setDarkMode(!darkMode);
    localStorage.setItem('darkMode', !darkMode);
  };

  // Load dark mode preference from localStorage
  useEffect(() => {
    const savedDarkMode = localStorage.getItem('darkMode') === 'true';
    setDarkMode(savedDarkMode);
  }, []);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <NotificationProvider>
          <SettingsProvider>
            <BookingProvider>
              <CompanyProvider>
                <CouponProvider>
                  <TripProvider>
                    <VehicleProvider>
                      <Router>
                        <Routes>
                          {/* Auth Routes */}
                          <Route path="/login" element={
                            <AuthLayout>
                              <LoginPage />
                            </AuthLayout>
                          } />
                          <Route path="/forgot-password" element={
                            <AuthLayout>
                              <ForgotPasswordPage />
                            </AuthLayout>
                          } />
                          <Route path="/two-factor-auth" element={
                            <AuthLayout>
                              <TwoFactorAuthPage />
                            </AuthLayout>
                          } />

                          {/* Identity Verification Routes */}
                          <Route path="/verification" element={
                            <PrivateRoute>
                              <AdminLayout toggleDarkMode={toggleDarkMode} darkMode={darkMode}>
                                <IdentityVerificationPage />
                              </AdminLayout>
                            </PrivateRoute>
                          } />
                          <Route path="/verification/submit" element={
                            <PrivateRoute>
                              <AdminLayout toggleDarkMode={toggleDarkMode} darkMode={darkMode}>
                                <SubmitVerificationPage />
                              </AdminLayout>
                            </PrivateRoute>
                          } />
                          <Route path="/verification/required" element={
                            <PrivateRoute>
                              <AdminLayout toggleDarkMode={toggleDarkMode} darkMode={darkMode}>
                                <VerificationRequired />
                              </AdminLayout>
                            </PrivateRoute>
                          } />

                          {/* Dashboard Routes */}
                          <Route path="/admin" element={
                            <PrivateRoute>
                              <AdminLayout toggleDarkMode={toggleDarkMode} darkMode={darkMode}>
                                <DashboardPage />
                              </AdminLayout>
                            </PrivateRoute>
                          } />
                          <Route path="/admin/dashboard/main" element={
                            <PrivateRoute>
                              <AdminLayout toggleDarkMode={toggleDarkMode} darkMode={darkMode}>
                                <MainDashboard />
                              </AdminLayout>
                            </PrivateRoute>
                          } />
                          <Route path="/admin/dashboard/bookings" element={
                            <PrivateRoute>
                              <AdminLayout toggleDarkMode={toggleDarkMode} darkMode={darkMode}>
                                <BookingsDashboard />
                              </AdminLayout>
                            </PrivateRoute>
                          } />
                          <Route path="/admin/dashboard/financial" element={
                            <PrivateRoute>
                              <AdminLayout toggleDarkMode={toggleDarkMode} darkMode={darkMode}>
                                <FinancialDashboard />
                              </AdminLayout>
                            </PrivateRoute>
                          } />
                          <Route path="/admin/dashboard/trips" element={
                            <PrivateRoute>
                              <AdminLayout toggleDarkMode={toggleDarkMode} darkMode={darkMode}>
                                <TripsDashboard />
                              </AdminLayout>
                            </PrivateRoute>
                          } />
                          <Route path="/admin/dashboard/vehicles" element={
                            <PrivateRoute>
                              <AdminLayout toggleDarkMode={toggleDarkMode} darkMode={darkMode}>
                                <VehiclesDashboard />
                              </AdminLayout>
                            </PrivateRoute>
                          } />

                          {/* Users Routes */}
                          <Route path="/admin/users" element={
                            <PrivateRoute>
                              <AdminLayout toggleDarkMode={toggleDarkMode} darkMode={darkMode}>
                                <UsersListPage />
                              </AdminLayout>
                            </PrivateRoute>
                          } />
                          <Route path="/admin/users/create" element={
                            <PrivateRoute>
                              <AdminLayout toggleDarkMode={toggleDarkMode} darkMode={darkMode}>
                                <UserCreatePage />
                              </AdminLayout>
                            </PrivateRoute>
                          } />
                          <Route path="/admin/users/:id" element={
                            <PrivateRoute>
                              <AdminLayout toggleDarkMode={toggleDarkMode} darkMode={darkMode}>
                                <UserDetailPage />
                              </AdminLayout>
                            </PrivateRoute>
                          } />
                          <Route path="/admin/users/:id/edit" element={
                            <PrivateRoute>
                              <AdminLayout toggleDarkMode={toggleDarkMode} darkMode={darkMode}>
                                <UserEditPage />
                              </AdminLayout>
                            </PrivateRoute>
                          } />
                          <Route path="/admin/users/:id/deactivate" element={
                            <PrivateRoute>
                              <AdminLayout toggleDarkMode={toggleDarkMode} darkMode={darkMode}>
                                <UserDeactivatePage />
                              </AdminLayout>
                            </PrivateRoute>
                          } />

                          {/* Companies Routes */}
                          <Route path="/admin/companies" element={
                            <PrivateRoute>
                              <AdminLayout toggleDarkMode={toggleDarkMode} darkMode={darkMode}>
                                <CompaniesListPage />
                              </AdminLayout>
                            </PrivateRoute>
                          } />
                          <Route path="/admin/companies/create" element={
                            <PrivateRoute>
                              <AdminLayout toggleDarkMode={toggleDarkMode} darkMode={darkMode}>
                                <CompanyCreatePage />
                              </AdminLayout>
                            </PrivateRoute>
                          } />
                          <Route path="/admin/companies/:id" element={
                            <PrivateRoute>
                              <AdminLayout toggleDarkMode={toggleDarkMode} darkMode={darkMode}>
                                <CompanyDetailPage />
                              </AdminLayout>
                            </PrivateRoute>
                          } />
                          <Route path="/admin/companies/:id/edit" element={
                            <PrivateRoute>
                              <AdminLayout toggleDarkMode={toggleDarkMode} darkMode={darkMode}>
                                <CompanyEditPage />
                              </AdminLayout>
                            </PrivateRoute>
                          } />
                          <Route path="/admin/companies/:id/deactivate" element={
                            <PrivateRoute>
                              <AdminLayout toggleDarkMode={toggleDarkMode} darkMode={darkMode}>
                                <CompanyDeactivatePage />
                              </AdminLayout>
                            </PrivateRoute>
                          } />

                          {/* Vehicles Routes */}
                          <Route path="/admin/vehicles" element={
                            <PrivateRoute>
                              <AdminLayout toggleDarkMode={toggleDarkMode} darkMode={darkMode}>
                                <VehiclesListPage />
                              </AdminLayout>
                            </PrivateRoute>
                          } />
                          <Route path="/admin/vehicles/create" element={
                            <PrivateRoute>
                              <AdminLayout toggleDarkMode={toggleDarkMode} darkMode={darkMode}>
                                <VehicleCreatePage />
                              </AdminLayout>
                            </PrivateRoute>
                          } />
                          <Route path="/admin/vehicles/:id" element={
                            <PrivateRoute>
                              <AdminLayout toggleDarkMode={toggleDarkMode} darkMode={darkMode}>
                                <VehicleDetailPage />
                              </AdminLayout>
                            </PrivateRoute>
                          } />
                          <Route path="/admin/vehicles/:id/edit" element={
                            <PrivateRoute>
                              <AdminLayout toggleDarkMode={toggleDarkMode} darkMode={darkMode}>
                                <VehicleEditPage />
                              </AdminLayout>
                            </PrivateRoute>
                          } />
                          <Route path="/admin/vehicles/:id/deactivate" element={
                            <PrivateRoute>
                              <AdminLayout toggleDarkMode={toggleDarkMode} darkMode={darkMode}>
                                <VehicleDeactivatePage />
                              </AdminLayout>
                            </PrivateRoute>
                          } />

                          {/* Routes Routes */}
                          <Route path="/admin/routes" element={
                            <PrivateRoute>
                              <AdminLayout toggleDarkMode={toggleDarkMode} darkMode={darkMode}>
                                <RoutesListPage />
                              </AdminLayout>
                            </PrivateRoute>
                          } />
                          <Route path="/admin/routes/create" element={
                            <PrivateRoute>
                              <AdminLayout toggleDarkMode={toggleDarkMode} darkMode={darkMode}>
                                <RouteCreatePage />
                              </AdminLayout>
                            </PrivateRoute>
                          } />
                          <Route path="/admin/routes/:id" element={
                            <PrivateRoute>
                              <AdminLayout toggleDarkMode={toggleDarkMode} darkMode={darkMode}>
                                <RouteDetailPage />
                              </AdminLayout>
                            </PrivateRoute>
                          } />
                          <Route path="/admin/routes/:id/edit" element={
                            <PrivateRoute>
                              <AdminLayout toggleDarkMode={toggleDarkMode} darkMode={darkMode}>
                                <RouteEditPage />
                              </AdminLayout>
                            </PrivateRoute>
                          } />
                          <Route path="/admin/routes/:id/deactivate" element={
                            <PrivateRoute>
                              <AdminLayout toggleDarkMode={toggleDarkMode} darkMode={darkMode}>
                                <RouteDeactivatePage />
                              </AdminLayout>
                            </PrivateRoute>
                          } />

                          {/* Trips Routes */}
                          <Route path="/admin/trips" element={
                            <PrivateRoute>
                              <AdminLayout toggleDarkMode={toggleDarkMode} darkMode={darkMode}>
                                <TripsListPage />
                              </AdminLayout>
                            </PrivateRoute>
                          } />
                          <Route path="/admin/trips/create" element={
                            <PrivateRoute>
                              <AdminLayout toggleDarkMode={toggleDarkMode} darkMode={darkMode}>
                                <TripCreatePage />
                              </AdminLayout>
                            </PrivateRoute>
                          } />
                          <Route path="/admin/trips/:id" element={
                            <PrivateRoute>
                              <AdminLayout toggleDarkMode={toggleDarkMode} darkMode={darkMode}>
                                <TripDetailPage />
                              </AdminLayout>
                            </PrivateRoute>
                          } />
                          <Route path="/admin/trips/:id/edit" element={
                            <PrivateRoute>
                              <AdminLayout toggleDarkMode={toggleDarkMode} darkMode={darkMode}>
                                <TripEditPage />
                              </AdminLayout>
                            </PrivateRoute>
                          } />
                          <Route path="/admin/trips/:id/cancel" element={
                            <PrivateRoute>
                              <AdminLayout toggleDarkMode={toggleDarkMode} darkMode={darkMode}>
                                <TripCancelPage />
                              </AdminLayout>
                            </PrivateRoute>
                          } />

                          {/* Bookings Routes */}
                          <Route path="/admin/bookings" element={
                            <PrivateRoute>
                              <AdminLayout toggleDarkMode={toggleDarkMode} darkMode={darkMode}>
                                <BookingsListPage />
                              </AdminLayout>
                            </PrivateRoute>
                          } />
                          <Route path="/admin/bookings/create" element={
                            <PrivateRoute>
                              <AdminLayout toggleDarkMode={toggleDarkMode} darkMode={darkMode}>
                                <BookingCreatePage />
                              </AdminLayout>
                            </PrivateRoute>
                          } />
                          <Route path="/admin/bookings/:id" element={
                            <PrivateRoute>
                              <AdminLayout toggleDarkMode={toggleDarkMode} darkMode={darkMode}>
                                <BookingDetailPage />
                              </AdminLayout>
                            </PrivateRoute>
                          } />
                          <Route path="/admin/bookings/:id/edit" element={
                            <PrivateRoute>
                              <AdminLayout toggleDarkMode={toggleDarkMode} darkMode={darkMode}>
                                <BookingEditPage />
                              </AdminLayout>
                            </PrivateRoute>
                          } />
                          <Route path="/admin/bookings/:id/cancel" element={
                            <PrivateRoute>
                              <AdminLayout toggleDarkMode={toggleDarkMode} darkMode={darkMode}>
                                <BookingCancelPage />
                              </AdminLayout>
                            </PrivateRoute>
                          } />

                          {/* Coupons Routes */}
                          <Route path="/admin/coupons" element={
                            <PrivateRoute>
                              <AdminLayout toggleDarkMode={toggleDarkMode} darkMode={darkMode}>
                                <CouponsListPage />
                              </AdminLayout>
                            </PrivateRoute>
                          } />
                          <Route path="/admin/coupons/create" element={
                            <PrivateRoute>
                              <AdminLayout toggleDarkMode={toggleDarkMode} darkMode={darkMode}>
                                <CouponCreatePage />
                              </AdminLayout>
                            </PrivateRoute>
                          } />
                          <Route path="/admin/coupons/:id" element={
                            <PrivateRoute>
                              <AdminLayout toggleDarkMode={toggleDarkMode} darkMode={darkMode}>
                                <CouponDetailPage />
                              </AdminLayout>
                            </PrivateRoute>
                          } />
                          <Route path="/admin/coupons/:id/edit" element={
                            <PrivateRoute>
                              <AdminLayout toggleDarkMode={toggleDarkMode} darkMode={darkMode}>
                                <CouponEditPage />
                              </AdminLayout>
                            </PrivateRoute>
                          } />

                          {/* Commissions Routes */}
                          <Route path="/admin/commissions" element={
                            <PrivateRoute>
                              <AdminLayout toggleDarkMode={toggleDarkMode} darkMode={darkMode}>
                                <CommissionsListPage />
                              </AdminLayout>
                            </PrivateRoute>
                          } />
                          <Route path="/admin/commissions/create" element={
                            <PrivateRoute>
                              <AdminLayout toggleDarkMode={toggleDarkMode} darkMode={darkMode}>
                                <CommissionCreatePage />
                              </AdminLayout>
                            </PrivateRoute>
                          } />
                          <Route path="/admin/commissions/:id" element={
                            <PrivateRoute>
                              <AdminLayout toggleDarkMode={toggleDarkMode} darkMode={darkMode}>
                                <CommissionDetailPage />
                              </AdminLayout>
                            </PrivateRoute>
                          } />
                          <Route path="/admin/commissions/:id/edit" element={
                            <PrivateRoute>
                              <AdminLayout toggleDarkMode={toggleDarkMode} darkMode={darkMode}>
                                <CommissionEditPage />
                              </AdminLayout>
                            </PrivateRoute>
                          } />

                          {/* Transactions Routes */}
                          <Route path="/admin/transactions" element={
                            <PrivateRoute>
                              <AdminLayout toggleDarkMode={toggleDarkMode} darkMode={darkMode}>
                                <TransactionsListPage />
                              </AdminLayout>
                            </PrivateRoute>
                          } />
                          <Route path="/admin/transactions/create" element={
                            <PrivateRoute>
                              <AdminLayout toggleDarkMode={toggleDarkMode} darkMode={darkMode}>
                                <TransactionCreatePage />
                              </AdminLayout>
                            </PrivateRoute>
                          } />
                          <Route path="/admin/transactions/:id" element={
                            <PrivateRoute>
                              <AdminLayout toggleDarkMode={toggleDarkMode} darkMode={darkMode}>
                                <TransactionDetailPage />
                              </AdminLayout>
                            </PrivateRoute>
                          } />
                          <Route path="/admin/transactions/:id/edit" element={
                            <PrivateRoute>
                              <AdminLayout toggleDarkMode={toggleDarkMode} darkMode={darkMode}>
                                <TransactionEditPage />
                              </AdminLayout>
                            </PrivateRoute>
                          } />

                          {/* Settings Routes */}
                          <Route path="/admin/settings" element={
                            <PrivateRoute>
                              <AdminLayout toggleDarkMode={toggleDarkMode} darkMode={darkMode}>
                                <SettingsPage />
                              </AdminLayout>
                            </PrivateRoute>
                          } />

                          {/* Default redirect */}
                          <Route path="/" element={<Navigate to="/admin" replace />} />
                        </Routes>
                      </Router>
                    </VehicleProvider>
                  </TripProvider>
                </CouponProvider>
              </CompanyProvider>
            </BookingProvider>
          </SettingsProvider>
        </NotificationProvider>
      </AuthProvider>
    </ThemeProvider>
  );
};

export default App;
